import * as browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { createSafeOriginUrl, dismissCommand, getBehavior, getCommand, getContent, isCommandExpired, } from "@eyeo-fragments/in-product-messaging/background";
import { getCampaignConfig, start as setupCampaignConfigs, } from "@eyeo-fragments/opd-persistence-experiment/background";
import { Prefs } from "../../alias/prefs";
import { port } from "../../../adblockplusui/adblockpluschrome/lib/messaging/port";
import { TabSessionStorage } from "../../../adblockplusui/adblockpluschrome/lib/storage/tab-session";
import { EventEmitter } from "../../../adblockplusui/adblockpluschrome/lib/events";
import { getLocaleInfo } from "../../i18n/background";
import * as logger from "../../utilities/background";
import { getSettings } from "../../prefs/background/settings";
import { isCookieBannerDialog, isDialog, isDialogBehavior, isDialogContent } from "./dialog";
import { isPingMessage } from "../shared";
import { setDialogCommandHandler } from "./middleware";
import { clearStats, getStats, setStats } from "./stats";
import { ShowOnpageDialogResult, coolDownPeriodKey, lastShownKey, } from "./tab-manager.types";
import { shouldBeDismissed, shouldBeShown, start as setupTimings } from "./timing";
const assignedDialogs = new TabSessionStorage("onpage-dialog:dialogs");
export const eventEmitter = new EventEmitter();
export const unassignedDialogs = new Map();
export const pagesWithCookieBanner = new Set();
const serverLogger = new ewe.telemetry.ServerLogger("onpage_dialog");
const uiServerLogger = new ewe.telemetry.ServerLogger("onpage_dialog_ui");
const persistentOpdLogger = new ewe.telemetry.ServerLogger("persistent_opd");
function logPersistentOpdEvent(event, dialog, displayCount, reason) {
    var _a;
    if (getCampaignConfig(dialog.ipmId) === null) {
        return;
    }
    const ipmId = (_a = dialog.ipmId) !== null && _a !== void 0 ? _a : "";
    persistentOpdLogger.behavior(event, typeof reason === "string" ? { ipmId, displayCount, reason } : { ipmId, displayCount }, dialog.ipmId);
}
function getErrorMessage(ex) {
    if (ex instanceof Error) {
        return ex.message;
    }
    if (typeof ex === "object" &&
        ex !== null &&
        "message" in ex &&
        typeof ex.message === "string") {
        return ex.message;
    }
    return "";
}
function classifyInjectionFailure(ex) {
    const message = getErrorMessage(ex);
    if (message.includes("Receiving end does not exist") ||
        message.includes("Could not establish connection")) {
        return "no_receiver";
    }
    if (message.includes("No tab with id")) {
        return "no_tab";
    }
    return "other";
}
function logDisplayDuration(dialog, cause) {
    const { lastDisplayTime } = getStats(dialog.id);
    if (lastDisplayTime === 0) {
        return;
    }
    const durationMs = Date.now() - lastDisplayTime;
    uiServerLogger.debug("dialog_display_duration", {
        terminationCause: cause,
        durationMs,
        ipmId: dialog.ipmId,
    });
}
export function compareDialogsByPriority(dialogA, dialogB) {
    var _a, _b;
    if (dialogA.behavior.priority > dialogB.behavior.priority) {
        return -1;
    }
    if (dialogA.behavior.priority < dialogB.behavior.priority) {
        return 1;
    }
    const lowPriorityFallbackValue = "z";
    const ipmIdA = (_a = dialogA.ipmId) !== null && _a !== void 0 ? _a : lowPriorityFallbackValue;
    const ipmIdB = (_b = dialogB.ipmId) !== null && _b !== void 0 ? _b : lowPriorityFallbackValue;
    if (ipmIdA < ipmIdB) {
        return -1;
    }
    if (ipmIdA > ipmIdB) {
        return 1;
    }
    return 0;
}
export async function isCoolDownPeriodOngoing() {
    await Prefs.untilLoaded;
    return Prefs.get(lastShownKey) + Prefs.get(coolDownPeriodKey) > Date.now();
}
function sendMessage(tabId, message) {
    return browser.tabs.sendMessage(tabId, message, { frameId: 0 });
}
function dismissDialog(dialog) {
    logger.debug("[onpage-dialog]: Dismiss dialog");
    unassignedDialogs.delete(dialog.id);
    if (typeof dialog.ipmId === "string") {
        dismissCommand(dialog.ipmId);
    }
}
async function removeDialogFrame(tabId) {
    const message = { type: "onpage-dialog.hide" };
    await sendMessage(tabId, message);
}
async function removeDialog(tabId, dialog, options = {}) {
    const { keepStorage = false, userDismissed = false } = options;
    logger.debug("[onpage-dialog]: Remove dialog");
    if (!isDialog(dialog)) {
        logger.debug("[onpage-dialog]: Remove dialog, no dialog found");
        return;
    }
    try {
        await removeDialogFrame(tabId);
        await assignedDialogs.delete(tabId);
    }
    catch (ex) {
    }
    if (keepStorage) {
        return;
    }
    const campaignConfig = getCampaignConfig(dialog.ipmId);
    const stats = getStats(dialog.id);
    if (userDismissed && campaignConfig !== null) {
        dismissDialog(dialog);
        clearStats(dialog.id);
        return;
    }
    if (!shouldBeDismissed(dialog, stats, campaignConfig === null || campaignConfig === void 0 ? void 0 : campaignConfig.maxImpressions)) {
        logger.debug("[onpage-dialog]: Keep dialog active");
        return;
    }
    if (!userDismissed) {
        logPersistentOpdEvent("exhausted", dialog, stats.displayCount);
    }
    dismissDialog(dialog);
    clearStats(dialog.id);
}
async function handleDialogCommand(ipmId) {
    if (typeof ipmId !== "string") {
        return;
    }
    const behavior = getBehavior(ipmId);
    if (!isDialogBehavior(behavior)) {
        return;
    }
    const content = getContent(ipmId);
    if (!isDialogContent(content)) {
        return;
    }
    const dialog = { behavior, content, id: ipmId, ipmId };
    unassignedDialogs.set(dialog.id, dialog);
    serverLogger.behavior("command_ready", ipmId, ipmId);
}
async function handleCloseMessage(message, sender) {
    var _a, _b;
    if (typeof ((_a = sender.page) === null || _a === void 0 ? void 0 : _a.id) === "undefined") {
        return;
    }
    const dialog = await assignedDialogs.get(sender.page.id);
    if (!isDialog(dialog)) {
        return;
    }
    const { displayCount } = getStats(dialog.id);
    void removeDialog(sender.page.id, dialog, { userDismissed: true });
    uiServerLogger.behavior("dialog_closed", (_b = dialog.ipmId) !== null && _b !== void 0 ? _b : "", dialog.ipmId);
    logPersistentOpdEvent("closed", dialog, displayCount);
}
async function handleContinueMessage(message, sender) {
    var _a, _b;
    if (typeof ((_a = sender.page) === null || _a === void 0 ? void 0 : _a.id) === "undefined") {
        return;
    }
    const dialog = await assignedDialogs.get(sender.page.id);
    if (!isDialog(dialog)) {
        return;
    }
    const safeTargetUrl = createSafeOriginUrl(dialog.behavior.target);
    if (safeTargetUrl === null) {
        return;
    }
    void browser.tabs.create({ url: safeTargetUrl });
    const { displayCount } = getStats(dialog.id);
    void removeDialog(sender.page.id, dialog, { userDismissed: true });
    uiServerLogger.behavior("dialog_button_clicked", (_b = dialog.ipmId) !== null && _b !== void 0 ? _b : "", dialog.ipmId);
    logPersistentOpdEvent("clicked", dialog, displayCount);
}
async function handleGetMessage(message, sender) {
    var _a;
    logger.debug("[onpage-dialog]: get");
    if (typeof ((_a = sender.page) === null || _a === void 0 ? void 0 : _a.id) === "undefined") {
        return null;
    }
    const dialog = await assignedDialogs.get(sender.page.id);
    if (!isDialog(dialog)) {
        logger.debug("[onpage-dialog]: get, no dialog");
        return null;
    }
    return {
        content: dialog.content,
        localeInfo: getLocaleInfo(),
    };
}
async function handlePingMessage(message, sender) {
    var _a, _b;
    if (!isPingMessage(message) || typeof ((_a = sender.page) === null || _a === void 0 ? void 0 : _a.id) === "undefined") {
        return;
    }
    const dialog = await assignedDialogs.get(sender.page.id);
    if (!isDialog(dialog)) {
        logger.debug("[onpage-dialog]: ping message, no dialog");
        return;
    }
    if (dialog.behavior.displayDuration === 0 ||
        message.displayDuration < dialog.behavior.displayDuration) {
        logger.debug("[onpage-dialog]: ping message, duration exceeded");
        return;
    }
    const { displayCount } = getStats(dialog.id);
    void removeDialog(sender.page.id, dialog);
    uiServerLogger.behavior("dialog_ignored", (_b = dialog.ipmId) !== null && _b !== void 0 ? _b : "", dialog.ipmId);
    logPersistentOpdEvent("ignored", dialog, displayCount, "display_duration_elapsed");
}
function handleTabRemovedEvent(data) {
    var _a;
    const { tabId, value: dialog } = data;
    logger.debug("[onpage-dialog]: Handle Tab Removed Event");
    if (!isDialog(dialog)) {
        logger.debug("[onpage-dialog]: Handle Tab Removed Event, no dialog");
        return;
    }
    const { displayCount } = getStats(dialog.id);
    logDisplayDuration(dialog, "tab_removed");
    void removeDialog(tabId, dialog);
    uiServerLogger.behavior("dialog_ignored", (_a = dialog.ipmId) !== null && _a !== void 0 ? _a : "", dialog.ipmId);
    logPersistentOpdEvent("ignored", dialog, displayCount, "tab_removed");
}
function handleTabNavigatedEvent(data) {
    const { value: dialog } = data;
    if (!isDialog(dialog)) {
        return;
    }
    logDisplayDuration(dialog, "tab_navigated");
    if (getCampaignConfig(dialog.ipmId) === null) {
        return;
    }
    logPersistentOpdEvent("ignored", dialog, getStats(dialog.id).displayCount, "tab_navigated");
}
async function injectDialog(tabId) {
    logger.debug("[onpage-dialog]: Injecting dialog");
    if (browser.scripting) {
        await browser.scripting.insertCSS({
            files: ["adblock-onpage-dialog-user.css"],
            origin: "USER",
            target: { tabId },
        });
        await browser.scripting.executeScript({
            files: ["onpage-dialog.postload.js"],
            target: { tabId },
        });
    }
    else {
        await browser.tabs.insertCSS(tabId, {
            file: "adblock-onpage-dialog-user.css",
            allFrames: false,
            cssOrigin: "user",
            runAt: "document_start",
        });
        await browser.tabs.executeScript(tabId, {
            file: "onpage-dialog.postload.js",
            allFrames: false,
        });
    }
}
async function shouldBeIgnored() {
    if (Prefs.get("marketing_opt_out") === true) {
        logger.debug("[onpage-dialog]: marketing_opt_out - enabled");
        return true;
    }
    if (getSettings().show_survey === false) {
        logger.debug("[onpage-dialog]: show_survey - disabled");
        return true;
    }
    if (getSettings().suppress_surveys) {
        logger.debug("[onpage-dialog]: suppress_surveys - enabled");
        return true;
    }
    if (getSettings().onpageMessages === false) {
        logger.debug("[onpage-dialog]: onpageMessages - disabled");
        return true;
    }
    return false;
}
export async function showOnpageDialog(tabId, tab, dialog) {
    var _a;
    if (await shouldBeIgnored()) {
        logger.debug("[onpage-dialog]: User ignores notifications");
        return ShowOnpageDialogResult.rejected;
    }
    const campaignConfig = getCampaignConfig(dialog.ipmId);
    if ((campaignConfig === null || campaignConfig === void 0 ? void 0 : campaignConfig.ignoreCooldown) !== true && (await isCoolDownPeriodOngoing())) {
        logger.debug("[onpage-dialog]: Cool down period still ongoing");
        return ShowOnpageDialogResult.ignored;
    }
    const stats = getStats(dialog.id);
    const shouldDialogBeDismissed = shouldBeDismissed(dialog, stats, campaignConfig === null || campaignConfig === void 0 ? void 0 : campaignConfig.maxImpressions);
    const shouldDialogBeShown = await shouldBeShown(tab, dialog, stats);
    if (shouldDialogBeDismissed) {
        logger.debug("[onpage-dialog]: Reject dialog");
        logPersistentOpdEvent("exhausted", dialog, stats.displayCount);
        return ShowOnpageDialogResult.rejected;
    }
    if (shouldDialogBeShown && (await assignedDialogs.has(tabId))) {
        logger.debug("[onpage-dialog]: Tab already contains dialog");
        return campaignConfig === null
            ? ShowOnpageDialogResult.rejected
            : ShowOnpageDialogResult.ignored;
    }
    if (!shouldDialogBeShown) {
        logger.debug("[onpage-dialog]: Don't show");
        return ShowOnpageDialogResult.ignored;
    }
    if (isCookieBannerDialog(dialog) && !pagesWithCookieBanner.has(tabId)) {
        logger.debug("[onpage-dialog]: No cookie banner detected on page");
        return ShowOnpageDialogResult.ignored;
    }
    logger.debug("[onpage-dialog]: Show dialog");
    await assignedDialogs.set(tabId, dialog);
    const assignedDialogOnPage = await assignedDialogs.get(tabId);
    if (!isDialog(assignedDialogOnPage)) {
        logger.debug("[onpage-dialog]: Failed to be assigned to the page ", tabId);
        return ShowOnpageDialogResult.ignored;
    }
    const now = Date.now();
    const displayCount = getStats(dialog.id).displayCount + 1;
    setStats(dialog.id, {
        displayCount,
        lastDisplayTime: now,
    });
    if ((campaignConfig === null || campaignConfig === void 0 ? void 0 : campaignConfig.ignoreCooldown) !== true) {
        await Prefs.set(lastShownKey, now);
    }
    try {
        await injectDialog(tabId);
    }
    catch (ex) {
        logger.reportAndLogError(ex, "[onpage-dialog] Injection of dialog failed");
        logPersistentOpdEvent("injection_failed", dialog, displayCount, classifyInjectionFailure(ex));
        return ShowOnpageDialogResult.error;
    }
    uiServerLogger.behavior("dialog_injected", (_a = dialog.ipmId) !== null && _a !== void 0 ? _a : "", dialog.ipmId);
    logPersistentOpdEvent("rendered", dialog, displayCount);
    return ShowOnpageDialogResult.shown;
}
function isTabPage(candidate) {
    return (candidate !== null && typeof candidate === "object" && "id" in candidate && "url" in candidate);
}
export async function showDialogFromList(dialogs, tab, dismissDialogFn = dismissDialog) {
    if (!tab.id) {
        return;
    }
    for (const dialog of dialogs) {
        if (typeof dialog.ipmId === "string") {
            const command = getCommand(dialog.ipmId);
            if (command && isCommandExpired(command)) {
                logger.error("[onpage-dialog]: Command has expired.");
                dismissDialogFn(dialog);
                continue;
            }
        }
        if (isCookieBannerDialog(dialog)) {
            const subs = (await SubscriptionAdapter.getSubscriptionsMinusText());
            const cookieSubsbscriptionId = "cookies-premium";
            const isCookieSubscriptionActive = Object.keys(subs).includes(cookieSubsbscriptionId);
            if (isCookieSubscriptionActive) {
                logger.debug("[onpage-dialog]: Dismiss 'cookie banner' OPD due to Premium cookie subscription active");
                dismissDialogFn(dialog);
                continue;
            }
        }
        const result = await showOnpageDialog(tab.id, tab, dialog);
        if (result === ShowOnpageDialogResult.rejected) {
            dismissDialogFn(dialog);
        }
    }
}
async function handlePageLoadedEvent(page) {
    if (!isTabPage(page)) {
        return;
    }
    if (!page.id) {
        return;
    }
    try {
        const assigned = await assignedDialogs.getQueued(page.id);
        if (isDialog(assigned)) {
            if (getCampaignConfig(assigned.ipmId) === null) {
                logger.debug("[onpage-dialog]: Tab already contains dialog");
                return;
            }
            await assignedDialogs.delete(page.id);
            logDisplayDuration(assigned, "tab_navigated");
            logPersistentOpdEvent("ignored", assigned, getStats(assigned.id).displayCount, "tab_navigated");
        }
        await removeDialogFrame(page.id);
    }
    catch (ex) {
    }
    if (unassignedDialogs.size === 0) {
        logger.debug("[onpage-dialog]: No command");
        return;
    }
    let tab = null;
    try {
        tab = await browser.tabs.get(page.id);
    }
    catch (ex) {
        return;
    }
    if (tab.incognito || typeof tab.url !== "string" || !/^https?:/.test(tab.url)) {
        return;
    }
    if (new URL(tab.url).href !== page.url.href) {
        logger.debug("[onpage-dialog]: Tab has navigated away from page");
        return;
    }
    const dialogs = Array.from(unassignedDialogs.values()).sort(compareDialogsByPriority);
    void showDialogFromList(dialogs, tab);
}
export async function handleCookieBannerDetected(message, sender, processCookieBannerDialogs = showDialogFromList) {
    logger.debug("[onpage-dialog]: Cookie banner detected");
    const { page } = sender;
    let tab = null;
    try {
        tab = await browser.tabs.get(page.id);
    }
    catch (ex) {
        return;
    }
    if (!tab) {
        return;
    }
    if (!tab.id) {
        return;
    }
    pagesWithCookieBanner.add(tab.id);
    let dialogs = Array.from(unassignedDialogs.values()).sort(compareDialogsByPriority);
    dialogs = dialogs.filter((dialog) => isCookieBannerDialog(dialog));
    void processCookieBannerDialogs(dialogs, tab);
}
async function clearCommands() {
    unassignedDialogs.clear();
    const tabs = await browser.tabs.query({});
    for (const tab of tabs) {
        if (typeof tab.id === "undefined") {
            continue;
        }
        const dialog = await assignedDialogs.get(tab.id);
        await removeDialog(tab.id, dialog, { keepStorage: true });
    }
}
export async function start() {
    await setupTimings();
    port.on("onpage-dialog.close", handleCloseMessage);
    port.on("onpage-dialog.continue", handleContinueMessage);
    port.on("onpage-dialog.get", handleGetMessage);
    port.on("onpage-dialog.ping", handlePingMessage);
    port.on("cookie-banner.detected", handleCookieBannerDetected);
    ext.addTrustedMessageTypes(null, [
        "onpage-dialog.continue",
        "onpage-dialog.close",
        "onpage-dialog.get",
        "onpage-dialog.ping",
        "cookie-banner.detected",
    ]);
    assignedDialogs.on("tab-removed", handleTabRemovedEvent);
    assignedDialogs.on("tab-navigated", handleTabNavigatedEvent);
    ext.pages.onRemoved.addListener((tabId) => {
        if (pagesWithCookieBanner.has(tabId)) {
            pagesWithCookieBanner.delete(tabId);
        }
    });
    ext.pages.onLoaded.addListener(handlePageLoadedEvent);
    await setupCampaignConfigs(logger);
    setDialogCommandHandler(handleDialogCommand, clearCommands);
}
